import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useFindOne, useGlobalAction } from '@gadgetinc/react';
import { api } from '../api';

const VMAgentChat = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Load agent configuration
  const [{ data: agentConfig, fetching: loadingAgent, error: agentError }] = useFindOne(
    api.agentConfiguration, 
    agentId,
    {
      select: {
        id: true,
        name: true,
        description: true,
        instructions: { markdown: true, truncatedHTML: true },
        initiatorMessage: { markdown: true, truncatedHTML: true },
        isActive: true
      }
    }
  );

  // Agent router action for sending messages
  const [{ data: routerData, fetching: sendingMessage, error: routerError }, sendToAgent] = useGlobalAction(api.agentRouter);

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-resize textarea
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputMessage]);

  // Add initial message when agent loads
  useEffect(() => {
    if (agentConfig && messages.length === 0) {
      const welcomeMessage = {
        id: 'welcome',
        content: agentConfig.initiatorMessage?.markdown || `Hi! I'm ${agentConfig.name}. How can I help you today?`,
        sender: 'agent',
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
    }
  }, [agentConfig]);

  // Handle router response
  useEffect(() => {
    if (routerData && routerData.result) {
      const responseContent = routerData.result.content || routerData.result.output || routerData.result.response || 'No response received';
      const agentMessage = {
        id: Date.now() + '-agent',
        content: responseContent,
        sender: 'agent',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, agentMessage]);
    }
  }, [routerData]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || sendingMessage) return;

    // Add user message to chat
    const userMessage = {
      id: Date.now() + '-user',
      content: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    const currentMessage = inputMessage;
    setInputMessage('');

    // Send to VM agent
    try {
      await sendToAgent({
        agentConfigId: agentId,
        input: currentMessage,
        task: 'general_query',
        priority: 'immediate'
      });
    } catch (error) {
      console.error('Error sending message to VM agent:', error);
      const errorMessage = {
        id: Date.now() + '-error',
        content: 'Sorry, there was an error processing your message. Please try again.',
        sender: 'agent',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (loadingAgent) {
    return (
      <div className="chat-page loading">
        <div className="loading-container">
          <div className="spinner"></div>
          <h3>Loading agent...</h3>
        </div>
        <style jsx>{`
          .chat-page.loading {
            min-height: 100vh;
            background: #1a1a1a;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .loading-container {
            text-align: center;
            color: white;
          }
          .spinner {
            width: 40px;
            height: 40px;
            border: 4px solid rgba(255, 255, 255, 0.1);
            border-top: 4px solid #007bff;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (agentError || (agentConfig && !agentConfig.isActive)) {
    return (
      <div className="chat-page error">
        <div className="error-container">
          <h3>Agent Not Available</h3>
          <p>The requested agent could not be found or is currently inactive.</p>
          <Link to="/agents" className="back-link">← Back to Agents</Link>
        </div>
        <style jsx>{`
          .chat-page.error {
            min-height: 100vh;
            background: #1a1a1a;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
          }
          .error-container {
            text-align: center;
          }
          .error-container h3 {
            color: #ff4444;
            margin-bottom: 1rem;
          }
          .error-container p {
            color: #ccc;
            margin-bottom: 2rem;
          }
          .back-link {
            color: #007bff;
            text-decoration: none;
            padding: 0.5rem 1rem;
            border: 1px solid #007bff;
            border-radius: 4px;
          }
          .back-link:hover {
            background: #007bff;
            color: white;
          }
        `}</style>
      </div>
    );
  }

  if (!agentConfig) {
    return null;
  }

  return (
    <div className="chat-page">
      <div className="chat-header">
        <div className="header-left">
          <button onClick={() => navigate('/agents')} className="back-button">
            ←
          </button>
          <div className="agent-info">
            <h3>{agentConfig.name}</h3>
            <p>{agentConfig.description}</p>
          </div>
        </div>
      </div>

      <div className="messages-container">
        <div className="messages">
          {messages.map((message) => (
            <div key={message.id} className={`message ${message.sender}`}>
              <div className="message-content">
                {message.content}
              </div>
            </div>
          ))}
          {sendingMessage && (
            <div className="message agent">
              <div className="message-content typing">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="input-container">
        <div className="input-wrapper">
          <textarea
            ref={textareaRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            disabled={sendingMessage}
            rows={3}
          />
          <button 
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || sendingMessage}
            className="send-button"
          >
            Send
          </button>
        </div>
      </div>

      <style jsx>{`
        .chat-page {
          min-height: 100vh;
          background: #1a1a1a;
          color: white;
          display: flex;
          flex-direction: column;
        }

        .chat-header {
          background: #2d2d2d;
          padding: 1rem;
          border-bottom: 1px solid #404040;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .back-button {
          background: none;
          border: none;
          color: #007bff;
          font-size: 1.5rem;
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 4px;
        }

        .back-button:hover {
          background: rgba(0, 123, 255, 0.1);
        }

        .agent-info h3 {
          margin: 0;
          font-size: 1.2rem;
          color: white;
        }

        .agent-info p {
          margin: 0.25rem 0 0 0;
          font-size: 0.9rem;
          color: #ccc;
        }

        .messages-container {
          flex: 1;
          overflow-y: auto;
          padding: 2rem 1rem;
        }

        .messages {
          max-width: 800px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .message {
          display: flex;
        }

        .message.user {
          justify-content: flex-end;
        }

        .message.agent {
          justify-content: flex-start;
        }

        .message-content {
          max-width: 70%;
          padding: 1.25rem;
          border-radius: 12px;
          line-height: 1.5;
          white-space: pre-wrap;
        }

        .message.user .message-content {
          background: #007bff;
          color: white;
        }

        .message.agent .message-content {
          background: #2d2d2d;
          color: white;
          border: 1px solid #404040;
        }

        .message-content.typing {
          padding: 1rem;
        }

        .typing-indicator {
          display: flex;
          gap: 4px;
        }

        .typing-indicator span {
          width: 8px;
          height: 8px;
          background: #666;
          border-radius: 50%;
          animation: typing 1.4s infinite ease-in-out;
        }

        .typing-indicator span:nth-child(2) {
          animation-delay: 0.2s;
        }

        .typing-indicator span:nth-child(3) {
          animation-delay: 0.4s;
        }

        @keyframes typing {
          0%, 60%, 100% {
            transform: translateY(0);
          }
          30% {
            transform: translateY(-10px);
          }
        }

        .input-container {
          background: #2d2d2d;
          padding: 1rem;
          border-top: 1px solid #404040;
          margin-bottom: 1rem;
        }

        .input-wrapper {
          max-width: 800px;
          margin: 0 auto;
          display: flex;
          gap: 1rem;
          align-items: flex-end;
        }

        .input-wrapper textarea {
          flex: 1;
          background: #1a1a1a;
          border: 1px solid #404040;
          border-radius: 8px;
          padding: 1rem;
          color: white;
          font-size: 1rem;
          resize: none;
          min-height: 80px;
          max-height: 200px;
          font-family: inherit;
          overflow-y: auto;
        }

        .input-wrapper textarea:focus {
          outline: none;
          border-color: #007bff;
        }

        .input-wrapper textarea::placeholder {
          color: #666;
        }

        .input-wrapper textarea:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .send-button {
          background: #007bff;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 1rem 2rem;
          cursor: pointer;
          font-size: 1rem;
          min-height: 80px;
          align-self: flex-end;
        }

        .send-button:hover:not(:disabled) {
          background: #0056b3;
        }

        .send-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default VMAgentChat;